/**
 * Decides whether a Sentry event is known, non-actionable noise that should be
 * dropped in `beforeSend`. Kept as a pure function to be easy to unit-test.
 *
 * Two categories are dropped:
 *  1. Framework/transient errors that are never a code bug on our side (Firestore
 *     SDK assert, network/offline, push timing, old webview, Ionic outlet).
 *  2. `permission-denied` ONLY when a breadcrumb shows it came from one of our
 *     live listeners that is legitimately denied during the auth transition
 *     (logout) — `revenuecatCustomers`/`subscriptions`. Real rule regressions on
 *     user actions lack this signature and are still reported.
 */

interface MinimalSentryEvent {
  message?: string;
  exception?: {
    values?: Array<{
      type?: string;
      value?: string;
      stacktrace?: { frames?: Array<{ module?: string; filename?: string }> };
    }>;
  };
  breadcrumbs?: Array<{ message?: string; category?: string }> | null;
}

interface MinimalEventHint {
  originalException?: unknown;
}

const IGNORED_MESSAGE_PATTERNS: RegExp[] = [
  /INTERNAL ASSERTION FAILED/i,            // @firebase/firestore SDK bug (Unexpected state)
  /auth\/network-request-failed/i,         // transient network during auth
  /deadline-exceeded/i,                    // transient Firestore timeout
  /client is offline/i,                    // offline read
  /Failed to get document because the client is offline/i,
  /capacitorDidRegisterForRemoteNotifications not called/i, // iOS push timing
  /\.animate is not a function/i,          // old webview without Element.animate
  /Cannot activate an already activated outlet/i, // Ionic router reuse
];

// Live-listener paths that legitimately return permission-denied during logout.
const TRANSIENT_LISTENER_PATHS = ['revenuecatCustomers', 'subscriptions'];

function collectMessages(event: MinimalSentryEvent, hint?: MinimalEventHint): string {
  const parts: string[] = [];
  if (event.message) parts.push(event.message);
  for (const v of event.exception?.values ?? []) {
    if (v.type) parts.push(v.type);
    if (v.value) parts.push(v.value);
  }
  const hintError = hint?.originalException as { message?: unknown } | undefined;
  if (hintError && typeof hintError.message === 'string') parts.push(hintError.message);
  return parts.join(' || ');
}

export function isIgnorableSentryEvent(event: MinimalSentryEvent, hint?: MinimalEventHint): boolean {
  const messages = collectMessages(event, hint);

  if (IGNORED_MESSAGE_PATTERNS.some((re) => re.test(messages))) {
    return true;
  }

  // The Firestore WebChannel transport occasionally throws a JSON parse error
  // when the long-poll response is cut off (often a 504). Transient. Engines
  // word it differently: V8/Chrome "Unexpected end of JSON input" (BAR-HELPER-2D4),
  // WebKit/Safari "JSON Parse error: Unexpected EOF" (BAR-HELPER-2FA). Scoped to
  // firestore/webchannel frames so a real JSON.parse('') in OUR code is still
  // reported.
  if (/Unexpected end of JSON input|JSON Parse error: Unexpected EOF/i.test(messages)) {
    const frames = (event.exception?.values ?? []).flatMap((v) => v.stacktrace?.frames ?? []);
    const fromFirestoreTransport = frames.some((f) =>
      /webchannel|@firebase\/firestore/i.test(`${f.module ?? ''} ${f.filename ?? ''}`),
    );
    if (fromFirestoreTransport) return true;
  }

  // FirebaseError "internal" is a transient Firestore backend / real-time
  // Listen-channel error (BAR-HELPER-2HX): a run of successful Firestore
  // requests then a failed /Firestore/Listen GET, surfaced by the SDK as
  // "internal". Not our code; recovers on its own. Matched on exact type+value
  // so unrelated "internal" errors elsewhere are still reported.
  const isFirestoreInternal = (event.exception?.values ?? []).some(
    (v) => /firebase/i.test(v.type ?? '') && /^internal$/i.test((v.value ?? '').trim()),
  );
  if (isFirestoreInternal) return true;

  const isPermissionDenied = /permission-denied|insufficient permissions/i.test(messages);
  if (isPermissionDenied) {
    const recent = (event.breadcrumbs ?? []).slice(-12);
    const fromTransientListener = recent.some((b) =>
      typeof b.message === 'string'
      && b.message.includes('stream caught [')
      && TRANSIENT_LISTENER_PATHS.some((p) => b.message!.includes(`[${p}`)),
    );
    if (fromTransientListener) return true;
  }

  return false;
}
